/**
 * Class schedule times type definitions
 *
 * These types reflect the class schedule options available in the system.
 * Times are in Eastern Time (New York) and are converted to the user's local time for display.
 * Morning classes: 9:00 AM - 1:00 PM ET
 * Evening classes: 6:00 PM - 10:00 PM ET
 * Night classes: 9:00 PM - 1:00 AM ET
 */

export type ClassScheduleType = 'morning' | 'evening' | 'night'

export interface ClassSchedule {
  morning: boolean
  evening: boolean
  night: boolean
}

/**
 * The time slot's icon, a key the application maps to its page icon
 * (components/icons/regular/sunrise, sunset and moon; Phase 5 PR 5.1).
 */
export type ClassScheduleIcon = 'sunrise' | 'sunset' | 'moon'

export interface ClassTimeDisplayOptions {
  morning: {
    // Eastern time hours (New York timezone)
    etStartHour: number
    etEndHour: number
    icon: ClassScheduleIcon
    label: string
  }
  evening: {
    // Eastern time hours (New York timezone)
    etStartHour: number
    etEndHour: number
    icon: ClassScheduleIcon
    label: string
  }
  night: {
    // Eastern time hours (New York timezone)
    etStartHour: number
    etEndHour: number
    icon: ClassScheduleIcon
    label: string
  }
}

// Default values for class schedule times - in Eastern Time (New York)
export const CLASS_SCHEDULE_TIMES: ClassTimeDisplayOptions = {
  morning: {
    etStartHour: 9, // 9:00 AM ET
    etEndHour: 13, // 1:00 PM ET
    icon: 'sunrise',
    label: 'Morning',
  },
  evening: {
    etStartHour: 18, // 1:00 PM ET
    etEndHour: 22, // 6:00 PM ET
    icon: 'sunset',
    label: 'Evening',
  },
  night: {
    etStartHour: 21, // 9:00 PM ET
    etEndHour: 1, // 1:00 AM ET
    icon: 'moon',
    label: 'Night',
  },
}

/**
 * Convert Eastern Time (New York) hour to user's local time
 *
 * This function properly handles the conversion from Eastern Time (ET) to the user's
 * local timezone, automatically adjusting for Daylight Saving Time (DST) based on
 * the current date.
 *
 * @param {number} hour - Eastern Time hour (0-23)
 * @param {boolean} includeTimeZone - Whether to include timezone in output
 * @returns {string} Formatted time in user's local timezone
 */
export function convertETToLocalTime(
  hour: number,
  includeTimeZone = false,
): string {
  // Get the current date and create a reference date for Eastern Time
  const now = new Date()

  // Create a date object for the current date with the specified hour in ET
  const etDate = new Date(now)
  etDate.setHours(hour, 0, 0, 0)

  // Format the date using the Eastern timezone to get time parts
  const etFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  })

  // Get the formatted date/time in Eastern timezone
  const etString = etFormatter.format(etDate)

  // Parse this into a local Date object (which will interpret it in the local timezone)
  const etLocal = new Date(etString)

  // Adjust for DST differences between Eastern Time and local timezone
  // This is the magic part that handles the DST offset correctly
  const localTime = new Date()
  localTime.setHours(hour, 0, 0, 0)

  // Get the current offset between local and Eastern time
  const offsetDiff = (etLocal.getHours() - hour + 24) % 24

  // Create the final local time by adjusting for the timezone difference
  const localDate = new Date(now)
  localDate.setHours(hour - offsetDiff, 0, 0, 0)

  // Format for display in user's local timezone
  const displayOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    hour12: true,
    ...(includeTimeZone && {timeZoneName: 'short'}),
  }

  const formattedTime = new Intl.DateTimeFormat(
    undefined,
    displayOptions,
  ).format(localDate)

  // Remove the space between hour and AM/PM if present
  return formattedTime.replace(/(\d)( )(\w{2})/, '$1$3')
}
